import { MemberTags } from "@circle-react-uikit/MemberTags";
import { MemberTagsVisibleInMemberList } from "@circle-react/helpers/memberTagsHelper";
import { useProfileLinkProps } from "@circle-react/hooks/profile/useProfileLinkProps";

import { useConnectionButtonState } from "@/react/components/ConnectionButtons/hooks/useConnectionButtonState";
import { ConnectionNote } from "@/react/components/ProfileModalV3/Body/ConnectBanner/ConnectionNote";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { useSmScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { isCommunityMember, type Person } from "@/react/types/AudienceList";

import { MemberCardImage } from "../../CardView/MemberCardImage";
import { ExpandableBio } from "./ExpandableBio";
import { ListViewCardHeader } from "./ListViewCardHeader";
import { MobileCommunityMember } from "./MobileView";

export interface CommunityMemberProps {
  canReceiveDmFromCurrentMember?: boolean;
  communityMember: Person;
  isLastItem?: boolean;
  shouldShowHiddenMemberTooltip?: boolean;
}

const getMemberTagsVisibleInMemberList = (communityMember: Person) => {
  if (isCommunityMember(communityMember)) {
    return MemberTagsVisibleInMemberList({
      memberTags: communityMember.member_tags ?? [],
    });
  }

  return [];
};

export const CommunityMember = ({
  communityMember,
  shouldShowHiddenMemberTooltip = false,
  isLastItem = false,
  canReceiveDmFromCurrentMember,
}: CommunityMemberProps) => {
  const profileLinkProps = useProfileLinkProps({
    public_uid: communityMember.public_uid ?? "",
  });

  const visibleMemberTags = getMemberTagsVisibleInMemberList(communityMember);
  const isConnectEnabled = useIsConnectEnabled();

  const {
    isPendingReceived: isOtherUserSentConnection,
    connection: memberConnection,
  } = useConnectionButtonState(
    isCommunityMember(communityMember) ? communityMember : undefined,
  );

  const shouldShowConnectionNote =
    isOtherUserSentConnection && isConnectEnabled && memberConnection?.note;

  const isMobile = useSmScreenMediaQuery();

  if (isMobile) {
    return (
      <MobileCommunityMember
        canReceiveDmFromCurrentMember={canReceiveDmFromCurrentMember ?? false}
        communityMember={communityMember}
        isLastItem={isLastItem}
      />
    );
  }

  return (
    <li
      className="border-primary bg-primary group rounded-xl border-[1px] p-6 first:mt-4 first:md:mt-0"
      data-testid="community_member"
    >
      <a className="flex w-full gap-x-4" {...profileLinkProps}>
        <div style={{ aspectRatio: "1 / 1" }} className="h-16">
          <MemberCardImage
            name={communityMember.name ?? ""}
            src={communityMember.avatar_url ?? ""}
            className="!rounded-full"
          />
        </div>
        <section className="flex min-w-0 flex-1 flex-col gap-y-3">
          <div className="flex flex-col gap-y-3">
            <ListViewCardHeader
              communityMember={communityMember}
              shouldShowHiddenMemberTooltip={shouldShowHiddenMemberTooltip}
              canReceiveDmFromCurrentMember={canReceiveDmFromCurrentMember}
            />
            {shouldShowConnectionNote && (
              <div className="-mb-2">
                <ConnectionNote
                  note={memberConnection?.note}
                  lineClamp={1}
                  collapse
                  connectionId={memberConnection?.id?.toString()}
                  member={communityMember}
                />
              </div>
            )}
            {communityMember.bio && <ExpandableBio bio={communityMember.bio} />}
          </div>
          <MemberTags
            memberTags={visibleMemberTags}
            limit={2}
            memberId={communityMember.id}
          />
        </section>
      </a>
    </li>
  );
};
